import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { darkModeClass } from '../../utils/cn';
import styles from './Projects.module.css';
import modalStyles from './ProjectModal.module.css';

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debouncedValue;
};

const Projects = () => {
  const { isDarkMode } = useTheme();
  const { data, t, formatNumber } = useLanguage();
  const projectsData = data.projects;
  const [query, setQuery] = useState('');
  const [technology, setTechnology] = useState('All');
  const [selectedProject, setSelectedProject] = useState(null);
  const closeButtonRef = useRef(null);
  const debouncedQuery = useDebounce(query, 300);

  const technologies = useMemo(() => [
    'All',
    ...Array.from(new Set(projectsData.flatMap(project => project.technologies))).sort()
  ], [projectsData]);

  const filteredProjects = useMemo(() => {
    const normalizedQuery = debouncedQuery.trim().toLowerCase();
    return projectsData.filter(project => {
      const matchesTechnology = technology === 'All' || project.technologies.includes(technology);
      const searchableText = `${project.title} ${project.description} ${project.technologies.join(' ')}`.toLowerCase();
      return matchesTechnology && searchableText.includes(normalizedQuery);
    });
  }, [debouncedQuery, technology, projectsData]);

  useEffect(() => {
    if (!selectedProject) return undefined;

    const handleKeyDown = event => {
      if (event.key === 'Escape') setSelectedProject(null);
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);
    requestAnimationFrame(() => closeButtonRef.current?.focus());

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedProject]);

  const resetFilters = () => {
    setQuery('');
    setTechnology('All');
  };

  const openProject = project => setSelectedProject(project);
  const handleCardKeyDown = (event, project) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openProject(project);
    }
  };

  return (
    <section id="projects" className={`${styles.projects} ${darkModeClass(styles, isDarkMode)}`}>
      <div className={styles.container}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>{t('projects.title')}</h2>
          <p className={styles.sectionSubtitle}>{t('projects.subtitle')}</p>
        </div>

        <div className={styles.projectExplorer}>
          <label className={styles.searchField}>
            <i className="fas fa-search" aria-hidden="true"></i>
            <span className={styles.srOnly}>{t('command.search')}</span>
            <input type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder={t('projects.search')} />
          </label>
          <label className={styles.filterField}>
            <i className="fas fa-filter" aria-hidden="true"></i>
            <span className={styles.srOnly}>{t('projects.allTechnologies')}</span>
            <select value={technology} onChange={event => setTechnology(event.target.value)}>
              {technologies.map(item => <option key={item} value={item}>{item === 'All' ? t('projects.allTechnologies') : item}</option>)}
            </select>
          </label>
          <span className={styles.resultCount}>{formatNumber(filteredProjects.length)} {filteredProjects.length === 1 ? t('projects.result') : t('projects.results')}</span>
        </div>
        
        {filteredProjects.length > 0 ? (
          <div className={styles.projectsGrid}>
            {filteredProjects.map(project => (
              /* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */
              <article
                key={project.title}
                className={styles.projectCard}
                onClick={() => openProject(project)}
                onKeyDown={event => handleCardKeyDown(event, project)}
                tabIndex="0"
                aria-label={`${t('projects.viewCase')}: ${project.title}`}
              >
                <div
                  className={styles.projectImage}
                  style={{ '--project-screenshot': project.screenshot ? `url("${project.screenshot}")` : 'none' }}
                >
                  <div className={styles.imagePlaceholder}>
                    {project.logo ? (
                      <img src={project.logo} alt={`${project.title} logo`} />
                    ) : (
                      <i className={project.icon}></i>
                    )}
                  </div>
                  <span className={styles.viewDetails}>{t('projects.viewCase')} <i className="fas fa-arrow-up-right-from-square"></i></span>
                </div>
                <div className={styles.projectContent}>
                  <h3 className={styles.projectTitle}>{project.title}</h3>
                  <p className={styles.projectDescription}>{project.description}</p>
                  <div className={styles.techStack}>
                    {project.technologies.map(tech => (
                      <button key={tech} className={styles.techTag} onClick={event => { event.stopPropagation(); setTechnology(tech); }}>{tech}</button>
                    ))}
                  </div>
                  <div className={styles.projectLinks}>
                    {project.github && <a href={project.github} onClick={event => event.stopPropagation()} target="_blank" rel="noopener noreferrer" className={styles.projectLink}><i className="fab fa-github"></i> {t('projects.code')}</a>}
                    {project.liveDemo && <a href={project.liveDemo} onClick={event => event.stopPropagation()} target="_blank" rel="noopener noreferrer" className={`${styles.projectLink} ${styles.demoLink}`}><i className="fas fa-external-link-alt"></i> {t('projects.demo')}</a>}
                  </div>
                </div>
              </article>
              /* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */
            ))}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <i className="fas fa-folder-open"></i><h3>{t('projects.emptyTitle')}</h3><p>{t('projects.emptyText')}</p><button onClick={resetFilters}>{t('projects.clear')}</button>
          </div>
        )}
      </div>

      {selectedProject && createPortal(
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-element-interactions
        <div className={modalStyles.backdrop} onMouseDown={() => setSelectedProject(null)}>
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <div className={modalStyles.dialog} role="dialog" aria-modal="true" aria-labelledby="project-modal-title" tabIndex="-1" onKeyDown={(e) => { if (e.key === 'Escape') setSelectedProject(null); }} onMouseDown={event => event.stopPropagation()}>
            <button ref={closeButtonRef} className={modalStyles.close} onClick={() => setSelectedProject(null)} aria-label={t('projects.close')}><i className="fas fa-times"></i></button>
            <div
              className={modalStyles.visual}
              style={{ '--project-screenshot': selectedProject.screenshot ? `url("${selectedProject.screenshot}")` : 'none' }}
            >
              <div className={modalStyles.icon}>
                {selectedProject.logo ? (
                  <img src={selectedProject.logo} alt={`${selectedProject.title} logo`} />
                ) : (
                  <i className={selectedProject.icon}></i>
                )}
              </div>
              <span>{t('projects.selectedCase')}</span>
            </div>
            <div className={modalStyles.body}>
              <span className={modalStyles.eyebrow}>{t('projects.overview')}</span>
              <h3 id="project-modal-title">{selectedProject.title}</h3>
              <p>{selectedProject.description}</p>
              <div className={modalStyles.caseStudy} aria-label={t('projects.details')}>
                <article><span>01</span><div><h4>{t('projects.problem')}</h4><p>{selectedProject.problem}</p></div></article>
                <article><span>02</span><div><h4>{t('projects.approach')}</h4><p>{selectedProject.approach}</p></div></article>
                <article><span>03</span><div><h4>{t('projects.outcome')}</h4><p>{selectedProject.outcome}</p></div></article>
                <article><span>04</span><div><h4>{t('projects.role')}</h4><p>{selectedProject.role}</p></div></article>
              </div>
              <div className={modalStyles.section}>
                <h4>{t('projects.stack')}</h4>
                <div className={modalStyles.technologies}>{selectedProject.technologies.map(tech => <span key={tech}>{tech}</span>)}</div>
              </div>
              <div className={modalStyles.actions}>
                {selectedProject.liveDemo && <a href={selectedProject.liveDemo} target="_blank" rel="noopener noreferrer">{t('projects.viewLive')} <i className="fas fa-arrow-up-right-from-square"></i></a>}
                {selectedProject.github && <a href={selectedProject.github} target="_blank" rel="noopener noreferrer" className={modalStyles.secondaryAction}><i className="fab fa-github"></i> {t('projects.browseSource')}</a>}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </section>
  );
};

export default Projects;
